import { useState, useEffect, useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Input, Label, Select } from '@/components/ui'
import { Scale, ArrowRightLeft, Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui'

interface UnitDef {
  label: string
  value: string
  toBase: (v: number) => number
  fromBase: (v: number) => number
}

interface Category {
  name: string
  icon: string
  units: UnitDef[]
}

const categories: Category[] = [
  {
    name: 'Length',
    icon: '📏',
    units: [
      { label: 'Meter (m)', value: 'm', toBase: v => v, fromBase: v => v },
      { label: 'Kilometer (km)', value: 'km', toBase: v => v * 1000, fromBase: v => v / 1000 },
      { label: 'Centimeter (cm)', value: 'cm', toBase: v => v / 100, fromBase: v => v * 100 },
      { label: 'Millimeter (mm)', value: 'mm', toBase: v => v / 1000, fromBase: v => v * 1000 },
      { label: 'Mile (mi)', value: 'mi', toBase: v => v * 1609.344, fromBase: v => v / 1609.344 },
      { label: 'Yard (yd)', value: 'yd', toBase: v => v * 0.9144, fromBase: v => v / 0.9144 },
      { label: 'Foot (ft)', value: 'ft', toBase: v => v * 0.3048, fromBase: v => v / 0.3048 },
      { label: 'Inch (in)', value: 'in', toBase: v => v * 0.0254, fromBase: v => v / 0.0254 },
      { label: 'Nautical Mile', value: 'nmi', toBase: v => v * 1852, fromBase: v => v / 1852 },
      { label: 'Micrometer (μm)', value: 'um', toBase: v => v / 1e6, fromBase: v => v * 1e6 },
    ],
  },
  {
    name: 'Weight',
    icon: '⚖️',
    units: [
      { label: 'Kilogram (kg)', value: 'kg', toBase: v => v, fromBase: v => v },
      { label: 'Gram (g)', value: 'g', toBase: v => v / 1000, fromBase: v => v * 1000 },
      { label: 'Milligram (mg)', value: 'mg', toBase: v => v / 1e6, fromBase: v => v * 1e6 },
      { label: 'Pound (lb)', value: 'lb', toBase: v => v * 0.453592, fromBase: v => v / 0.453592 },
      { label: 'Ounce (oz)', value: 'oz', toBase: v => v * 0.0283495, fromBase: v => v / 0.0283495 },
      { label: 'Metric Ton (t)', value: 't', toBase: v => v * 1000, fromBase: v => v / 1000 },
      { label: 'Stone (st)', value: 'st', toBase: v => v * 6.35029, fromBase: v => v / 6.35029 },
    ],
  },
  {
    name: 'Temperature',
    icon: '🌡️',
    units: [
      { label: 'Celsius (°C)', value: 'C', toBase: v => v, fromBase: v => v },
      { label: 'Fahrenheit (°F)', value: 'F', toBase: v => (v - 32) * 5 / 9, fromBase: v => v * 9 / 5 + 32 },
      { label: 'Kelvin (K)', value: 'K', toBase: v => v - 273.15, fromBase: v => v + 273.15 },
    ],
  },
  {
    name: 'Volume',
    icon: '🧪',
    units: [
      { label: 'Liter (L)', value: 'L', toBase: v => v, fromBase: v => v },
      { label: 'Milliliter (mL)', value: 'mL', toBase: v => v / 1000, fromBase: v => v * 1000 },
      { label: 'Gallon (US)', value: 'gal', toBase: v => v * 3.78541, fromBase: v => v / 3.78541 },
      { label: 'Quart (US)', value: 'qt', toBase: v => v * 0.946353, fromBase: v => v / 0.946353 },
      { label: 'Pint (US)', value: 'pt', toBase: v => v * 0.473176, fromBase: v => v / 0.473176 },
      { label: 'Cup (US)', value: 'cup', toBase: v => v * 0.236588, fromBase: v => v / 0.236588 },
      { label: 'Fluid Ounce (US)', value: 'fl_oz', toBase: v => v * 0.0295735, fromBase: v => v / 0.0295735 },
      { label: 'Tablespoon', value: 'tbsp', toBase: v => v * 0.0147868, fromBase: v => v / 0.0147868 },
      { label: 'Teaspoon', value: 'tsp', toBase: v => v * 0.00492892, fromBase: v => v / 0.00492892 },
      { label: 'Cubic Meter (m³)', value: 'm3', toBase: v => v * 1000, fromBase: v => v / 1000 },
    ],
  },
  {
    name: 'Area',
    icon: '📐',
    units: [
      { label: 'Square Meter (m²)', value: 'm2', toBase: v => v, fromBase: v => v },
      { label: 'Square Kilometer (km²)', value: 'km2', toBase: v => v * 1e6, fromBase: v => v / 1e6 },
      { label: 'Square Foot (ft²)', value: 'ft2', toBase: v => v * 0.092903, fromBase: v => v / 0.092903 },
      { label: 'Square Yard (yd²)', value: 'yd2', toBase: v => v * 0.836127, fromBase: v => v / 0.836127 },
      { label: 'Square Mile (mi²)', value: 'mi2', toBase: v => v * 2.59e6, fromBase: v => v / 2.59e6 },
      { label: 'Acre', value: 'acre', toBase: v => v * 4046.86, fromBase: v => v / 4046.86 },
      { label: 'Hectare (ha)', value: 'ha', toBase: v => v * 10000, fromBase: v => v / 10000 },
    ],
  },
  {
    name: 'Speed',
    icon: '🏎️',
    units: [
      { label: 'Meter/Second (m/s)', value: 'm/s', toBase: v => v, fromBase: v => v },
      { label: 'Kilometer/Hour (km/h)', value: 'km/h', toBase: v => v / 3.6, fromBase: v => v * 3.6 },
      { label: 'Mile/Hour (mph)', value: 'mph', toBase: v => v * 0.44704, fromBase: v => v / 0.44704 },
      { label: 'Knot (kn)', value: 'kn', toBase: v => v * 0.514444, fromBase: v => v / 0.514444 },
      { label: 'Foot/Second (ft/s)', value: 'ft/s', toBase: v => v * 0.3048, fromBase: v => v / 0.3048 },
    ],
  },
  {
    name: 'Time',
    icon: '⏱️',
    units: [
      { label: 'Second (s)', value: 's', toBase: v => v, fromBase: v => v },
      { label: 'Millisecond (ms)', value: 'ms', toBase: v => v / 1000, fromBase: v => v * 1000 },
      { label: 'Minute (min)', value: 'min', toBase: v => v * 60, fromBase: v => v / 60 },
      { label: 'Hour (h)', value: 'h', toBase: v => v * 3600, fromBase: v => v / 3600 },
      { label: 'Day (d)', value: 'd', toBase: v => v * 86400, fromBase: v => v / 86400 },
      { label: 'Week (wk)', value: 'wk', toBase: v => v * 604800, fromBase: v => v / 604800 },
      { label: 'Month (approx)', value: 'mo', toBase: v => v * 2629746, fromBase: v => v / 2629746 },
      { label: 'Year (approx)', value: 'yr', toBase: v => v * 31556952, fromBase: v => v / 31556952 },
    ],
  },
  {
    name: 'Digital Storage',
    icon: '💾',
    units: [
      { label: 'Byte (B)', value: 'B', toBase: v => v, fromBase: v => v },
      { label: 'Kilobyte (KB)', value: 'KB', toBase: v => v * 1024, fromBase: v => v / 1024 },
      { label: 'Megabyte (MB)', value: 'MB', toBase: v => v * 1048576, fromBase: v => v / 1048576 },
      { label: 'Gigabyte (GB)', value: 'GB', toBase: v => v * 1073741824, fromBase: v => v / 1073741824 },
      { label: 'Terabyte (TB)', value: 'TB', toBase: v => v * 1099511627776, fromBase: v => v / 1099511627776 },
      { label: 'Bit (b)', value: 'b', toBase: v => v / 8, fromBase: v => v * 8 },
      { label: 'Kilobit (Kb)', value: 'Kb', toBase: v => v * 128, fromBase: v => v / 128 },
      { label: 'Megabit (Mb)', value: 'Mb', toBase: v => v * 131072, fromBase: v => v / 131072 },
    ],
  },
  {
    name: 'Pressure',
    icon: '🔧',
    units: [
      { label: 'Pascal (Pa)', value: 'Pa', toBase: v => v, fromBase: v => v },
      { label: 'Kilopascal (kPa)', value: 'kPa', toBase: v => v * 1000, fromBase: v => v / 1000 },
      { label: 'Bar', value: 'bar', toBase: v => v * 100000, fromBase: v => v / 100000 },
      { label: 'PSI', value: 'psi', toBase: v => v * 6894.76, fromBase: v => v / 6894.76 },
      { label: 'Atmosphere (atm)', value: 'atm', toBase: v => v * 101325, fromBase: v => v / 101325 },
      { label: 'mmHg', value: 'mmHg', toBase: v => v * 133.322, fromBase: v => v / 133.322 },
    ],
  },
  {
    name: 'Energy',
    icon: '⚡',
    units: [
      { label: 'Joule (J)', value: 'J', toBase: v => v, fromBase: v => v },
      { label: 'Kilojoule (kJ)', value: 'kJ', toBase: v => v * 1000, fromBase: v => v / 1000 },
      { label: 'Calorie (cal)', value: 'cal', toBase: v => v * 4.184, fromBase: v => v / 4.184 },
      { label: 'Kilocalorie (kcal)', value: 'kcal', toBase: v => v * 4184, fromBase: v => v / 4184 },
      { label: 'Watt-Hour (Wh)', value: 'Wh', toBase: v => v * 3600, fromBase: v => v / 3600 },
      { label: 'Kilowatt-Hour (kWh)', value: 'kWh', toBase: v => v * 3.6e6, fromBase: v => v / 3.6e6 },
      { label: 'Electron Volt (eV)', value: 'eV', toBase: v => v * 1.602e-19, fromBase: v => v / 1.602e-19 },
      { label: 'BTU', value: 'BTU', toBase: v => v * 1055.06, fromBase: v => v / 1055.06 },
    ],
  },
]

function formatNumber(n: number): string {
  if (n === 0) return '0'
  const abs = Math.abs(n)
  if (abs >= 1e15 || (abs < 1e-10 && abs > 0)) return n.toExponential(6)
  if (Number.isInteger(n)) return n.toLocaleString()
  // Smart decimal places
  const str = n.toPrecision(10)
  return parseFloat(str).toString()
}

export function UnitConverter() {
  const [categoryIdx, setCategoryIdx] = useState(0)
  const [fromUnit, setFromUnit] = useState('')
  const [toUnit, setToUnit] = useState('')
  const [fromValue, setFromValue] = useState('1')
  const [copied, setCopied] = useState(false)

  const category = categories[categoryIdx]
  const units = category.units

  // Reset units when category changes
  useEffect(() => {
    setFromUnit(units[0]?.value || '')
    setToUnit(units[1]?.value || units[0]?.value || '')
  }, [categoryIdx])

  const result = useMemo(() => {
    const num = parseFloat(fromValue)
    if (isNaN(num)) return ''
    const from = units.find(u => u.value === fromUnit)
    const to = units.find(u => u.value === toUnit)
    if (!from || !to) return ''
    const base = from.toBase(num)
    const converted = to.fromBase(base)
    return formatNumber(converted)
  }, [fromValue, fromUnit, toUnit, units])

  const fromLabel = units.find(u => u.value === fromUnit)?.label || ''
  const toLabel = units.find(u => u.value === toUnit)?.label || ''

  const swap = () => {
    setFromUnit(toUnit)
    setToUnit(fromUnit)
    if (result) setFromValue(result)
  }

  const copyResult = () => {
    if (!result) return
    navigator.clipboard.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20">
            <Scale className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Unit Converter</h1>
            <p className="text-muted-foreground">Convert between units across {categories.length} categories</p>
          </div>
        </div>
      </div>

      {/* Category selector */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat, i) => (
          <button
            key={cat.name}
            onClick={() => setCategoryIdx(i)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              i === categoryIdx
                ? 'bg-primary text-primary-foreground shadow-glow'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
            }`}
          >
            <span>{cat.icon}</span>
            <span className="hidden sm:inline">{cat.name}</span>
          </button>
        ))}
      </div>

      {/* Converter */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>{category.icon} {category.name} Converter</CardTitle>
          <CardDescription>Convert between {units.length} {category.name.toLowerCase()} units</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-[1fr_auto_1fr]">
            {/* From */}
            <div className="space-y-3">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">From</Label>
              <Select
                value={fromUnit}
                onChange={e => setFromUnit(e.target.value)}
              >
                {units.map(u => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </Select>
              <Input
                type="number"
                value={fromValue}
                onChange={e => setFromValue(e.target.value)}
                placeholder="Enter value"
                className="text-lg font-mono"
              />
            </div>

            {/* Swap button */}
            <div className="flex items-center justify-center">
              <Button
                variant="outline"
                size="icon"
                onClick={swap}
                className="rounded-full h-10 w-10 mt-6"
                title="Swap units"
              >
                <ArrowRightLeft className="h-4 w-4" />
              </Button>
            </div>

            {/* To */}
            <div className="space-y-3">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">To</Label>
              <Select
                value={toUnit}
                onChange={e => setToUnit(e.target.value)}
              >
                {units.map(u => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </Select>
              <div className="relative">
                <Input
                  readOnly
                  value={result}
                  placeholder="Result"
                  className="text-lg font-mono pr-10"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={copyResult}
                  disabled={!result}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-green-400" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Summary */}
          {fromValue && result && (
            <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-border">
              <p className="text-sm text-center font-mono">
                <span className="text-primary font-semibold">{fromValue}</span>{' '}
                <span className="text-muted-foreground">{fromLabel}</span>{' '}
                <span className="text-muted-foreground">=</span>{' '}
                <span className="text-primary font-semibold">{result}</span>{' '}
                <span className="text-muted-foreground">{toLabel}</span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick reference */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-base">Quick Reference — {category.name}</CardTitle>
          <CardDescription>Common conversions from 1 {units[0]?.label}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {units.slice(1).map(u => {
              const baseVal = units[0].toBase(1)
              const converted = u.fromBase(baseVal)
              return (
                <div
                  key={u.value}
                  className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/20 border border-border/50 text-sm"
                >
                  <span className="text-muted-foreground">{u.label}</span>
                  <span className="font-mono text-primary">{formatNumber(converted)}</span>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
